import { HttpResponse }      from '@angular/common/http';
import { Injectable }        from '@angular/core';
import { NormalizerService } from '@app/core/http/normalizer.service';
import { IList }             from '@app/core/interfaces/list-response.interfaces';
import {
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                            from '@core/interfaces/api.interface';
import {
	Observable,
	throwError,
	timer
}                            from 'rxjs';
import {
	catchError,
	map,
	mergeMap,
	retryWhen
}                            from 'rxjs/operators';


type EntitySanitizer = ( entity, context ) => void;
type EntityPreMapProcessor<DTO> = ( entity: DTO, context ) => DTO;
type EntityPostMapProcessor<Entity> = ( entity: Entity, context ) => Entity;

@Injectable( {
	providedIn: 'root'
} )
export class TransitionService {

	constructor(
		public readonly normalizer: NormalizerService
	) { }


	// MAPPING #########################################################
	mapListApiResponse<DTO, Entity>( listDTO: IList<DTO>, mapToClass: new() => Entity ): IList<Entity> {
		const mappedList: Entity[] = [];
		listDTO.results.forEach( ( entityDTO: DTO ) => {
			let entity: Entity = new mapToClass();
			entity = Object.assign( entity, entityDTO );
			mappedList.push( entity );
		} );

		const mappedListResponse: IList<Entity> = {
			results: mappedList,
			paging: listDTO?.paging,
			totalCount: listDTO?.totalCount
		};
		return mappedListResponse;
	}

	mapEntityApiResponse<DTO, Entity>(
		entityDTO: DTO | HttpResponse<DTO>,
		mapToClass?: new() => Entity
	): Entity {
		let entity: Entity = new mapToClass();
		// Handling OPTION withFullResponse
		if ( entityDTO instanceof HttpResponse ) {
			entity = Object.assign( entity, entityDTO.body as DTO );

			if ( entity.hasOwnProperty( 'etag' ) ) {
				// @ts-ignore
				entity.etag = entityDTO.headers.get( 'ETag' );
			}
			return entity;
		}

		// Handling default
		return Object.assign( entity, entityDTO );
	}

	mapForApiRequest<Entity, DTO>( entity: Entity, dto: DTO ): DTO {
		// maps dto properties only
		Object.keys( dto ).forEach( ( key ) => {
			dto[ key ] = entity[ key ];
			if ( !dto[ key ] === undefined ) { dto[ key ] = null; }
		} );

		return dto;
	}


	// RESPONSE HANDLING ##################################################
	// TODO maybe optimize/combine processors and sanitizer to one object config object
	handleListRequestResponse<DTO, Entity>(
		req$: Observable<IList<DTO>>,
		mapToClass: new() => Entity,
		context,
		sanitizer?: EntitySanitizer,
		preMapProcessor?: EntityPreMapProcessor<DTO>,
		postMapProcessor?: EntityPostMapProcessor<Entity>,
		errorIsThrown = false
	): Observable<RequestCompleteListState<Entity>> {

		return req$
		.pipe(
			// retry( 1 ),
			retryWhen( this.genericRetryStrategy() ),
			map( ( res: IList<DTO> ) => {
				if ( preMapProcessor ) {
					res.results = res.results.map( entity => preMapProcessor( entity, context ) );
				}
				const mappedListResponse = this.mapListApiResponse<DTO, Entity>( res, mapToClass );
				if ( postMapProcessor ) {
					mappedListResponse.results = mappedListResponse.results.map( entity => postMapProcessor( entity, context ) );
				}
				if ( sanitizer ) { mappedListResponse.results.map( entity => sanitizer( entity, context ) ); }
				return this.normalizer.handleListSuccessResponse<Entity>( mappedListResponse, RequestActionEnum.LIST );
			} ),
			catchError( error => this.normalizer.handleListErrorResponse<Entity>( error, RequestActionEnum.LIST, errorIsThrown ) )
		);
	}

	// TODO maybe optimize/combine processors and sanitizer to one config object
	handleEntityRequestResponse<DTO, Entity>(
		req$: Observable<DTO>,
		requestAction: RequestActionEnum,
		mapToClass: new() => Entity,
		context,
		sanitizer?: EntitySanitizer,
		preMapProcessor?: EntityPreMapProcessor<DTO>,
		postMapProcessor?: EntityPostMapProcessor<Entity>,
		forceEntity?: Entity,
		errorIsThrown = false
	): Observable<RequestCompleteEntityState<Entity>> {
		return req$.pipe(
			// retry( 1 ),
			retryWhen( this.genericRetryStrategy() ),
			map( ( res: DTO | HttpResponse<DTO> ) => {
				let dto = res instanceof HttpResponse ? res.body : res;

				if ( preMapProcessor ) { dto = preMapProcessor( dto, context ); }

				let entity;
				if ( res instanceof HttpResponse ) {
					const mappedDtoHttpResponse = new HttpResponse<DTO>( { ...res, ...{ body: dto } } );
					entity = this.mapEntityApiResponse<HttpResponse<DTO>, Entity>( mappedDtoHttpResponse, mapToClass );
				} else {
					entity = this.mapEntityApiResponse<DTO, Entity>( dto, mapToClass );
				}

				if ( postMapProcessor ) { entity = postMapProcessor( entity, context ); }
				if ( sanitizer ) { sanitizer( entity, context ); }

				if ( res instanceof HttpResponse ) {
					const entityHttpResponse = new HttpResponse<Entity>( { ...res, ...{ body: entity } } );
					return this.normalizer.handleEntitySuccessResponse<Entity>( entityHttpResponse, requestAction );
				} else {
					if ( forceEntity ) {
						entity = forceEntity;
					}
					return this.normalizer.handleEntitySuccessResponse<Entity>( entity, requestAction );
				}
			} ),
			catchError( error => this.normalizer.handleEntityErrorResponse( error, requestAction, errorIsThrown ) )
		);
	}

	prepareEntityRequest<Entity, DTO>(
		entity: Entity,
		mapToClass: new() => DTO,
		context,
		sanitizer?: EntitySanitizer,
		preMapProcessor?: EntityPreMapProcessor<Entity>,
		postMapProcessor?: EntityPostMapProcessor<DTO>
	): DTO {
		const dto: DTO = new mapToClass();
		this.mapForApiRequest<Entity, DTO>( entity, dto );
		if ( sanitizer ) { sanitizer( dto, context ); }
		return dto;
	}

	getEmptyRequestCompleteEntityState<Entity>(
		action: RequestActionEnum,
		requestState: RequestState = RequestState.NOT_ASKED
	): RequestCompleteEntityState<Entity> {
		return {
			action,
			requestState,
			entity: null
		};
	}

	getEmptyRequestCompleteListState<Entity>(
		action: RequestActionEnum,
		requestState: RequestState = RequestState.NOT_ASKED
	): RequestCompleteListState<Entity> {
		return {
			action,
			paging: null,
			requestState,
			list: null
		};
	}


	private genericRetryStrategy = ( {
		maxRetryAttempts = 1,
		scalingDuration = 1000
	}: {
		maxRetryAttempts?: number,
		scalingDuration?: number
	} = {} ) => ( attempts: Observable<any> ) => {
		return attempts.pipe(
			mergeMap( ( error, i ) => {
				const retryAttempt = i + 1;
				// if maximum number of retries have been met
				// or response is any status code other than 5xx we don't wish to retry and throw error
				if (
					retryAttempt > maxRetryAttempts ||
					error.status < 500
				) {
					return throwError( error );
				}

				// retry after 1s, 2s, etc...
				return timer( retryAttempt * scalingDuration );
			} )
		);
	};
}
